import {
  addAppCalendarDays,
  formatAppIntlLabel,
  getAppWallDateParts,
  parseDbDateTime,
} from "../../../utils/appDateTime";

/** Parse API/UI YYYY-MM-DD as a local calendar date (legacy Firestitch parity). */
export const parseAccountingLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return new Date(year, month, day);
};

export const formatAccountingLocalDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const addAccountingLocalDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

/** Monday-based accounting week start (legacy weekStartsOn: Monday). */
export const getAccountingWeekStartMonday = (date) => {
  const d = parseAccountingLocalDate(date);
  if (!d) return null;
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
};

const toIsoDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return value.trim().slice(0, 10);
  }
  return formatAccountingLocalDate(parseAccountingLocalDate(value));
};

/** ISO week number from a calendar date in the app display timezone. */
export const getWeekNumber = (date) => {
  const iso = toIsoDateKey(date);
  if (!iso) return null;
  const anchor = parseDbDateTime(`${iso}T12:00:00Z`);
  if (!anchor) return null;
  const parts = getAppWallDateParts(anchor);
  if (!parts) return null;
  const d = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

export const formatWeekChipLabel = (startDate, endDate) => {
  const startIso = toIsoDateKey(startDate);
  if (!startIso) return "";
  const startAnchor = parseDbDateTime(`${startIso}T12:00:00Z`);
  if (!startAnchor) return "";
  const candidateEndIso = toIsoDateKey(endDate);
  const candidateEndWeekStart = candidateEndIso
    ? toIsoDateKey(getAccountingWeekStartMonday(candidateEndIso))
    : "";
  const endIso =
    candidateEndIso && candidateEndWeekStart === startIso
      ? candidateEndIso
      : addAppCalendarDays(startIso, 6);
  const endAnchor = parseDbDateTime(`${endIso}T12:00:00Z`);
  const weekNo = getWeekNumber(startIso);
  const startText = formatAppIntlLabel(startAnchor, {
    month: "short",
    day: "numeric",
  });
  const endText = formatAppIntlLabel(endAnchor, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `Week: #${weekNo}: ${startText} - ${endText}`;
};

export const formatPayPeriodLabel = (startDate, endDate) => {
  const startIso = toIsoDateKey(startDate);
  if (!startIso) return "";
  const startAnchor = parseDbDateTime(`${startIso}T12:00:00Z`);
  if (!startAnchor) return "";
  const startText = formatAppIntlLabel(startAnchor, {
    month: "short",
    day: "numeric",
  });
  const endIso = toIsoDateKey(endDate);
  const endAnchor = endIso
    ? parseDbDateTime(`${endIso}T12:00:00Z`)
    : null;
  if (!endAnchor) {
    return formatAppIntlLabel(startAnchor, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const endDay = formatAppIntlLabel(endAnchor, { day: "numeric" });
  const endYear = formatAppIntlLabel(endAnchor, { year: "numeric" });
  return `${startText} \u2013 ${endDay}, ${endYear}`;
};

export const formatWeekPickerLabel = (startDate) => {
  const startIso = toIsoDateKey(startDate);
  if (!startIso) return "Select week...";
  const weekNo = getWeekNumber(startIso);
  const anchor = parseDbDateTime(`${startIso}T12:00:00Z`);
  const dateText = anchor
    ? formatAppIntlLabel(anchor, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : startIso;
  return `Week ${weekNo} (${dateText})`;
};

export const formatDateChipLabel = (dateValue) => {
  const iso = toIsoDateKey(dateValue);
  if (!iso) return "";
  const anchor = parseDbDateTime(`${iso}T12:00:00Z`);
  if (!anchor) return "";
  return `Date: ${formatAppIntlLabel(anchor, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
};

export const getStaffDisplayName = (staff) => {
  if (!staff) return "";
  const direct =
    staff.name ||
    staff.full_name ||
    staff.fullName ||
    staff.display_name ||
    staff.displayName ||
    "";
  if (direct) return String(direct).trim();

  const first =
    staff.first_name || staff.firstName || staff.given_name || staff.givenName || "";
  const last =
    staff.last_name || staff.lastName || staff.surname || staff.familyName || "";
  const combined = `${first || ""} ${last || ""}`.trim();
  return combined;
};
